import Highcharts from 'highcharts';
import BrokenAxisModule from 'highcharts/modules/broken-axis';
import HighchartsReact from 'highcharts-react-official';
import { useMemo, useRef, type SyntheticEvent } from 'react';
import { Alert, Button } from 'rsuite';
import { useElasticidadeCruzadaStore } from '../../ElasticidadeCruzada.store';
import type { ShareType } from '../../types';
import { buildMarketShareChartOptions } from './chartConfig';
import { LEGEND_COMPETITOR_TRUNCATE_MAX_VIEWPORT_WIDTH } from './chartHelpers';
import { useWindowSize } from '../../../../../../hooks/useWindowSize';
import { getIndicatorsContext, getSeriesByMetric, hasBulletAtX } from './chartHelpers';
import styles from './styles.module.scss';
import { OPTIONS_DROPDOWN } from '../../../ModuloOmnichannel/constants';
import { OptionsDropdown } from '../../../ModuloOmnichannel/components/OptionsDropdown';
import { downloadElasticityCurves } from '../../services';
import { extractApiErrorMessage } from '../../../../../../utils/extractApiErrorMessage';

const COMPETITOR_SERIES_KEY_BY_SHARE_TYPE: Record<ShareType, 'revenue' | 'volume'> = {
  VALOR: 'revenue',
  VOLUME: 'volume',
};

BrokenAxisModule(Highcharts);

export const MarketShareChart = () => {
  const { marketShareResponse, competitorDisplayMode, setCompetitorDisplayMode, curvesPayload, tipoConcorrente, calculoConcorrente } =
    useElasticidadeCruzadaStore();
  const chartRef = useRef<HighchartsReact.RefObject>(null);
  const { width: viewportWidth } = useWindowSize();
  const shouldTruncateCompetitorLegendNames = viewportWidth <= LEGEND_COMPETITOR_TRUNCATE_MAX_VIEWPORT_WIDTH;

  const baseChart = marketShareResponse?.chart;
  const insights = marketShareResponse?.insights;

  const chartData = useMemo(() => {
    if (!baseChart) return null;
    const isAverageScenario = tipoConcorrente === 'TODOS' && calculoConcorrente === 'MEDIA';
    const byMetric = isAverageScenario ? baseChart.competitorAverageSeriesByMetric : baseChart.competitorSeriesByMetric;
    const competitorSeries = competitorDisplayMode ? byMetric[COMPETITOR_SERIES_KEY_BY_SHARE_TYPE[competitorDisplayMode]] : [];
    return { ...baseChart, competitorSeries };
  }, [baseChart, competitorDisplayMode, tipoConcorrente, calculoConcorrente]);

  const chartOptions = useMemo<Highcharts.Options | null>(() => {
    if (!chartData || !insights) return null;

    const seriesContext = getSeriesByMetric(chartData);
    const indicatorsContext = getIndicatorsContext(chartData);
    const currentUsesSeriesColor = insights.current
      ? hasBulletAtX(seriesContext.shareVolumeData, insights.current.x) || hasBulletAtX(seriesContext.shareValueData, insights.current.x)
      : false;
    const optimalUsesSeriesColor = insights.optimal
      ? hasBulletAtX(seriesContext.shareVolumeData, insights.optimal.x) || hasBulletAtX(seriesContext.shareValueData, insights.optimal.x)
      : false;

    return buildMarketShareChartOptions({
      chartData,
      insights,
      seriesContext,
      indicatorsContext,
      currentUsesSeriesColor,
      optimalUsesSeriesColor,
      curvesClient: marketShareResponse?.client ?? null,
      competitorDisplayMode,
      shouldTruncateCompetitorLegendNames,
    });
  }, [chartData, insights, marketShareResponse?.client, competitorDisplayMode, shouldTruncateCompetitorLegendNames]);

  const handleCompetitorModeClick = (mode: ShareType, event: SyntheticEvent) => {
    if (competitorDisplayMode === mode) {
      setCompetitorDisplayMode(null);
      (event.currentTarget as HTMLElement).blur();
      return;
    }
    setCompetitorDisplayMode(mode);
  };

  const handleClickDownload = (key: 'csv' | 'xlsx') => {
    const format = key === 'csv' ? 'CSV' : 'XLSX';
    if (!curvesPayload) return;
    void downloadElasticityCurves(format, curvesPayload)
      .then(result => {
        if (result && typeof result === 'object' && 'status' in result && (result as { status: number }).status !== 200) {
          Alert.error('Erro ao baixar o arquivo. Tente novamente.', 5000);
        }
      })
      .catch((error: unknown) => {
        const message = extractApiErrorMessage(error) || 'Erro ao baixar o arquivo. Tente novamente.';
        Alert.error(message, 5000);
      });
  };

  if (!chartData || !chartOptions) return null;

  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <div className={styles.headerText}>
          <h3 className={styles.title}>Projeção de market share</h3>
          <p className={styles.description}>
            Competitividade do produto referência VS market share, comparado com o produto selecionado e os demais concorrentes
          </p>
        </div>
        <OptionsDropdown id="market-share-more-options" data={OPTIONS_DROPDOWN} onClick={handleClickDownload} />
      </div>
      <div className={styles.displayCompetitors}>
        <span className={styles.displayCompetitorsText}>EXIBIR CONCORRENTES:</span>
        <Button
          appearance="subtle"
          className={`${styles.displayCompetitorsButton}${competitorDisplayMode === 'VALOR' ? ` ${styles.displayCompetitorsButtonActive}` : ''}`}
          onClick={event => handleCompetitorModeClick('VALOR', event)}
        >
          Por valor de vendas
        </Button>
        <Button
          appearance="subtle"
          className={`${styles.displayCompetitorsButton}${competitorDisplayMode === 'VOLUME' ? ` ${styles.displayCompetitorsButtonActive}` : ''}`}
          onClick={event => handleCompetitorModeClick('VOLUME', event)}
        >
          Por volume de vendas
        </Button>
      </div>
      <div className={styles.chartContainer}>
        <HighchartsReact ref={chartRef} highcharts={Highcharts} options={chartOptions} />
      </div>
    </div>
  );
};
